import * as THREE from "three";
import { BX, CY, G, UP, V3, along, anchors, latheTube, mesh, n, pipe, rod } from "./core";

/** @formigen/geometry — primitives. Each is a pure (params, material, materials) => THREE.Group. */

export function box(p, mat) {
  const g = G();
  const w = n(p.w, 1), h = n(p.h, 1), d = n(p.d, 1);
  g.add(mesh(BX(w, h, d), mat, 0, p.anchor === "center" ? 0 : h / 2, 0));
  return anchors(g, { base: [0, 0, 0], top: [0, p.anchor === "center" ? h / 2 : h, 0], tip: [0, h, 0] });
}

export function cylinder(p, mat) {
  const g = G();
  const h = n(p.h, 1), r = n(p.r, 0.5);
  g.add(mesh(CY(r, h, n(p.segments, 24), p.rb, !!p.open), mat, 0, p.anchor === "center" ? 0 : h / 2, 0));
  return anchors(g, { base: [0, 0, 0], top: [0, h, 0], tip: [0, h, 0] });
}

export function tube(p, mat) {
  const g = G();
  g.add(latheTube(n(p.ri, 0.4), n(p.ro, 0.5), 0, n(p.h, 1), mat, n(p.segments, 56)));
  return anchors(g, { base: [0, 0, 0], top: [0, n(p.h, 1), 0] });
}

export function lathe(p, mat) {
  const g = G();
  const pts = (p.points || []).map((q) => new THREE.Vector2(q[0], q[1]));
  g.add(mesh(new THREE.LatheGeometry(pts, n(p.segments, 32)), mat));
  const top = pts.length ? pts[pts.length - 1].y : 0;
  return anchors(g, { base: [0, 0, 0], tip: [0, top, 0] });
}

export function torus(p, mat) {
  const g = G();
  const t = mesh(new THREE.TorusGeometry(n(p.r, 0.3), n(p.tube, 0.03), 12, n(p.segments, 48)), mat);
  if (p.plane !== "xy") t.rotation.x = Math.PI / 2;
  g.add(t);
  return anchors(g, { base: [0, 0, 0] });
}

export function dished_head(p, mat) {
  const g = G();
  const r = n(p.r, 1);
  const d = mesh(new THREE.SphereGeometry(r, n(p.segments, 40), 12, 0, Math.PI * 2, 0, Math.PI / 2), mat);
  d.scale.set(1, n(p.ratio, 0.28), 1);
  if (p.down) d.rotation.x = Math.PI;
  g.add(d);
  return anchors(g, { base: [0, 0, 0], tip: [0, r * n(p.ratio, 0.28), 0] });
}

export function flange(p, mat, M) {
  const g = G();
  const t = n(p.t, 0.05);
  g.add(mesh(CY(n(p.r, 0.2), t, 28), mat, 0, t / 2, 0));
  if (p.cap_r) g.add(mesh(CY(p.cap_r, n(p.cap_t, 0.03), 28), M.tank || mat, 0, t + n(p.cap_t, 0.03) / 2, 0));
  return anchors(g, { base: [0, 0, 0], top: [0, t + n(p.cap_t, 0), 0] });
}

export function nozzle(p, mat, M) {
  const g = G();
  const h = n(p.h, 0.2), r = n(p.r, 0.08);
  g.add(mesh(CY(r, h, 20), mat, 0, h / 2, 0));
  g.add(mesh(CY(n(p.flange_r, r * 1.8), n(p.flange_t, 0.02), 24), M.metal || mat, 0, h + n(p.flange_t, 0.02) / 2, 0));
  return anchors(g, { base: [0, 0, 0], nozzle_face: [0, h + n(p.flange_t, 0.02), 0], tip: [0, h, 0] });
}

export function valve(p, mat, M) {
  const g = G();
  const dir = n(p.dir, 1), len = n(p.len, 0.26), axis = p.axis || "x";
  const a = V3(0, 0, 0);
  const b = axis === "z" ? V3(0, 0, dir * len) : axis === "y" ? V3(0, dir * len, 0) : V3(dir * len, 0, 0);
  g.add(rod(a, b, n(p.r, 0.06), mat));
  const w = mesh(new THREE.TorusGeometry(n(p.wheel_r, 0.1), 0.016, 8, 20), M.red || mat);
  w.position.copy(b);
  if (axis === "x") w.rotation.y = Math.PI / 2;
  else if (axis === "y") w.rotation.x = Math.PI / 2;
  g.add(w);
  return anchors(g, { base: [0, 0, 0], tip: [b.x, b.y, b.z] });
}

export function pipe_run(p, mat) {
  const g = G();
  pipe(g, p.points || [], n(p.r, 0.05), mat);
  const last = (p.points || [])[(p.points || []).length - 1] || [0, 0, 0];
  return anchors(g, { base: (p.points || [])[0] || [0, 0, 0], tip: last });
}

export function ladder(p, mat) {
  const g = G();
  const h = n(p.h, 3), w = n(p.width, 0.4), so = n(p.standoff, 0.15);
  for (const z of [-w / 2, w / 2]) g.add(rod(V3(0, 0, z), V3(0, h, z), n(p.rail_r, 0.025), mat, 8));
  for (let y = n(p.pitch, 0.3); y < h - 0.1; y += n(p.pitch, 0.3))
    g.add(rod(V3(0, y, -w / 2), V3(0, y, w / 2), n(p.rung_r, 0.014), mat, 6));
  for (const z of [-w / 2, w / 2]) g.add(rod(V3(-so, h * 0.53, z), V3(0, h * 0.53, z), 0.02, mat, 6));
  return anchors(g, { base: [0, 0, 0], top: [0, h, 0] });
}

export function platform(p, mat) {
  const g = G();
  const w = n(p.w, 1.2), d = n(p.d, 0.8), t = n(p.t, 0.04);
  g.add(mesh(BX(w, t, d), mat, 0, t / 2, 0));
  const rh = n(p.rail_h, 0);
  if (rh) {
    for (const sx of [-1, 1]) for (const sz of [-1, 1])
      g.add(rod(V3(sx * (w / 2 - 0.05), t, sz * (d / 2 - 0.05)), V3(sx * (w / 2 - 0.05), t + rh, sz * (d / 2 - 0.05)), 0.02, mat, 6));
    g.add(mesh(BX(w, 0.03, 0.03), mat, 0, t + rh, -d / 2 + 0.05));
  }
  return anchors(g, { base: [0, 0, 0], top: [0, t, 0] });
}

export function cabinet(p, mat, M) {
  const g = G();
  const w = n(p.w, 0.5), h = n(p.h, 1.2), d = n(p.d, 0.4);
  g.add(mesh(BX(w, h, d), mat, 0, h / 2, 0));
  if (p.door) g.add(mesh(BX(0.012, h * 0.88, d * 0.02 + 0.008), M.dark || mat, w / 2 + 0.006, h / 2, 0));
  return anchors(g, { base: [0, 0, 0], top: [0, h, 0] });
}

export function dial_gauge(p, mat, M) {
  const g = G();
  const t = n(p.t, 0.06);
  g.add(mesh(CY(n(p.r, 0.11), t, 24), mat));
  g.add(mesh(CY(n(p.face_r, 0.09), n(p.face_t, 0.01), 24), M.gauge || mat, 0, t / 2 + n(p.face_t, 0.01) / 2, 0));
  return anchors(g, { base: [0, -t / 2, 0], tip: [0, t / 2 + n(p.face_t, 0.01), 0] });
}

export function nameplate(p, mat) {
  const g = G();
  g.add(mesh(BX(n(p.t, 0.01), n(p.h, 0.3), n(p.w, 0.45)), mat));
  return anchors(g, { base: [0, 0, 0] });
}

export function section_extrude(p, mat) {
  const g = G();
  const shape = new THREE.Shape((p.profile || []).map((q) => new THREE.Vector2(q[0], q[1])));
  const geo = new THREE.ExtrudeGeometry(shape, { depth: n(p.length, 1), bevelEnabled: false });
  const m = mesh(geo, mat);
  if (p.axis === "x") m.rotation.y = Math.PI / 2;
  else if (p.axis === "y") m.rotation.x = -Math.PI / 2;
  g.add(m);
  return anchors(g, { base: [0, 0, 0] });
}

export const PRIMITIVES = {
  box, cylinder, tube, lathe, torus, dished_head, flange, nozzle, valve, pipe_run,
  ladder, platform, cabinet, dial_gauge, nameplate, section_extrude,
};
